import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { analyticsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  ArrowLeft, BarChart3, LogOut, Wand2, Download
} from 'lucide-react';
import { MobileNav } from '../components/MobileNav';
import { LogoHome } from '../components/LogoHome';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";
const COLORS = ['#0D9488', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const AVAILABLE_METRICS = [
  { id: 'reports_count', label: 'Report Counts', description: 'Total, submitted, approved, draft' },
  { id: 'compliance_avg', label: 'Compliance Scores', description: 'Average, min, max scores' },
  { id: 'worker_breakdown', label: 'Worker Breakdown', description: 'Per-worker report counts' },
  { id: 'template_breakdown', label: 'Template Usage', description: 'Reports per template type' },
  { id: 'clock_hours', label: 'Clock Hours', description: 'Shift hours and counts' },
  { id: 'wellbeing', label: 'Wellbeing Data', description: 'Mood and stress trends' },
];

export default function ReportBuilderPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedMetrics, setSelectedMetrics] = useState(['reports_count', 'compliance_avg']);
  const [groupBy, setGroupBy] = useState('date');
  const [days, setDays] = useState('30');
  const [generating, setGenerating] = useState(false);
  const [reportData, setReportData] = useState(null);

  const toggleMetric = (id) => {
    setSelectedMetrics(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const handleGenerate = async () => {
    if (selectedMetrics.length === 0) {
      toast.error('Select at least one metric');
      return;
    }
    setGenerating(true);
    try {
      const res = await analyticsApi.generateCustomReport({
        metrics: selectedMetrics.join(','),
        group_by: groupBy,
        days: parseInt(days),
      });
      setReportData(res.data);
      toast.success('Report generated');
    } catch (err) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };
  const backRoute = user?.role === 'organisation' ? '/admin' : '/supervisor';

  const navItems = [
    { to: backRoute, label: 'Back', icon: <ArrowLeft className="h-5 w-5" /> },
    { to: '/analytics', label: 'Analytics', icon: <BarChart3 className="h-5 w-5" /> },
    { to: '/report-builder', label: 'Report Builder', icon: <Wand2 className="h-5 w-5" />, active: true },
  ];

  const formatLabel = (val) => {
    if (!val) return 'N/A';
    return String(val).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="report-builder-page">
      <MobileNav navItems={navItems} user={user} onLogout={handleLogout} />

      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-950 border-r border-slate-800 z-50 hidden lg:flex lg:flex-col">
        <div className="p-6 pb-2 flex-shrink-0">
          <LogoHome className="h-16 w-auto mb-4" />
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-2">
          <nav className="space-y-1">
            <Link to={backRoute} className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <ArrowLeft className="h-5 w-5" /> Dashboard
            </Link>
            <Link to="/analytics" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <BarChart3 className="h-5 w-5" /> Analytics
            </Link>
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-teal-600/20 text-teal-400 border border-teal-600/30">
              <Wand2 className="h-5 w-5" /> Report Builder
            </div>
          </nav>
        </div>
        <div className="flex-shrink-0 p-6 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-medium">{user?.name?.charAt(0)}</div>
            <div><p className="text-white font-medium text-sm">{user?.name}</p><p className="text-slate-500 text-xs capitalize">{user?.role}</p></div>
          </div>
          <Button variant="outline" size="sm" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      <main className="lg:pl-64">
        <div className="p-6 lg:p-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Custom Report Builder</h1>
          <p className="text-slate-400 mb-8">Select metrics and parameters to generate a custom analytics report</p>

          {/* Configuration */}
          <Card className="bg-slate-800/50 border-slate-700 mb-6">
            <CardHeader><CardTitle className="text-white text-base">Report Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              {/* Metrics Selection */}
              <div>
                <Label className="text-slate-300 text-sm mb-3 block">Select Metrics</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {AVAILABLE_METRICS.map(m => (
                    <div
                      key={m.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleMetric(m.id)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleMetric(m.id)}
                      className={`text-left p-3 rounded-lg border transition-colors cursor-pointer ${
                        selectedMetrics.includes(m.id)
                          ? 'border-teal-500 bg-teal-600/10 text-teal-400'
                          : 'border-slate-700 bg-slate-900/50 text-slate-400 hover:border-slate-600'
                      }`}
                      data-testid={`metric-${m.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox checked={selectedMetrics.includes(m.id)} className="pointer-events-none" />
                        <span className="text-sm font-medium">{m.label}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 ml-6">{m.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Group By & Period */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300 text-sm">Group By</Label>
                  <Select value={groupBy} onValueChange={setGroupBy}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1" data-testid="group-by-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="worker">Worker</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="template_type">Template Type</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Period</Label>
                  <Select value={days} onValueChange={setDays}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                      <SelectItem value="365">Last Year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button onClick={handleGenerate} disabled={generating || selectedMetrics.length === 0} className="bg-teal-600 hover:bg-teal-700" data-testid="generate-report-btn">
                <Wand2 className="h-4 w-4 mr-2" />
                {generating ? 'Generating...' : 'Generate Report'}
              </Button>
            </CardContent>
          </Card>

          {/* Results */}
          {reportData && (
            <div className="space-y-6" data-testid="report-results">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Report Results</h2>
                <p className="text-sm text-slate-500">Grouped by {reportData.group_by} | Last {reportData.period_days} days</p>
              </div>

              {/* Reports Count Chart */}
              {reportData.data.reports_count && reportData.data.reports_count.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white text-base">Report Counts</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={reportData.data.reports_count.map(r => ({ ...r, label: r.label || formatLabel(r.group) }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="label" stroke="#94A3B8" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                        <Legend />
                        <Bar dataKey="count" fill="#0D9488" name="Total" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="submitted" fill="#3B82F6" name="Submitted" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="approved" fill="#10B981" name="Approved" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Compliance Chart */}
              {reportData.data.compliance_avg && reportData.data.compliance_avg.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white text-base">Compliance Scores</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={reportData.data.compliance_avg.map(r => ({ ...r, label: formatLabel(r.group) }))}>
                        <defs>
                          <linearGradient id="cg2" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0D9488" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#0D9488" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="label" stroke="#94A3B8" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} domain={[0, 100]} />
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                        <Area type="monotone" dataKey="avg" stroke="#0D9488" fill="url(#cg2)" strokeWidth={2} name="Avg Score" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Worker Breakdown */}
              {reportData.data.worker_breakdown && reportData.data.worker_breakdown.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white text-base">Worker Breakdown</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={reportData.data.worker_breakdown} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis type="number" stroke="#94A3B8" tick={{ fontSize: 11 }} />
                        <YAxis dataKey="worker_name" type="category" stroke="#94A3B8" tick={{ fontSize: 11 }} width={100} />
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                        <Bar dataKey="count" fill="#3B82F6" name="Reports" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Template Breakdown */}
              {reportData.data.template_breakdown && reportData.data.template_breakdown.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white text-base">Template Usage</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie data={reportData.data.template_breakdown.map(t => ({ ...t, name: formatLabel(t.template_type) }))}
                          cx="50%" cy="50%" outerRadius={100} dataKey="count"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                          {reportData.data.template_breakdown.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Clock Hours */}
              {reportData.data.clock_hours && reportData.data.clock_hours.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white text-base">Clock Hours</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={reportData.data.clock_hours}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="date" stroke="#94A3B8" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                        <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                        <Bar dataKey="hours" fill="#F59E0B" name="Hours" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Wellbeing */}
              {reportData.data.wellbeing && reportData.data.wellbeing.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white text-base">Wellbeing Trends</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={reportData.data.wellbeing}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="date" stroke="#94A3B8" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                        <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} domain={[0, 10]} />
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                        <Legend />
                        <Line type="monotone" dataKey="avg_mood" stroke="#10B981" strokeWidth={2} name="Mood" />
                        <Line type="monotone" dataKey="avg_stress" stroke="#EF4444" strokeWidth={2} name="Stress" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Empty state if all selected metrics returned no data */}
              {Object.values(reportData.data).every(v => !v || v.length === 0) && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-12 text-center">
                    <BarChart3 className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No data available for the selected metrics and period</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
